import React from 'react';
import { t, currentLanguage } from '../i18n';

// Shared constants + small helpers for the Vendor Ledger and the compliance
// (insurance / license expiry) tracking that gates vendor payments.

// How a payment went out. Mirrors the "Mode of Payment" column in the old
// spreadsheet. Admin-only, so these stay English.
export const PAYMENT_MODES = [
  'Cash', 'Check', 'Zelle', 'Venmo', 'PayPal', 'Cash App',
  'ACH / Bank Transfer', 'Credit Card', 'Other'
];

// The three documents a tech must keep current to stay payable, plus a
// catch-all. Values must stay in sync with server/compliance.js; labels are
// translated at render time via lg.type.*
export const CREDENTIAL_TYPES = ['insurance', 'business-license', 'occupational-license', 'w9', 'other'];

// Documents with no expiry date. A W-9 is signed once and stays good until the
// business's details change, so it is on file or it is not — there is no
// renewal to chase and no date to show. Mirrors UNDATED_TYPES in
// server/compliance.js; the two lists have to agree or the vault and the
// payment gate will disagree about whether a tech is clear.
export const UNDATED_TYPES = ['w9'];
export const neverExpires = (type) => UNDATED_TYPES.includes(type);

// Documents not every trade holds. A handyman needs no occupational licence; a
// sole operator may carry no city business licence. Absent is fine; lapsed is
// not. Mirrors OPTIONAL_TYPES in server/compliance.js, and the two lists have
// to agree — the server decides whether a tech gets paid, and this one only
// decides what colour they see.
export const OPTIONAL_TYPES = ['occupational-license', 'business-license'];
export const isOptional = (type) => OPTIONAL_TYPES.includes(type);

export const credentialLabel = (type) => t('lg.type.' + type, type);

export const LEDGER_STATUSES = ['open', 'in-progress', 'completed', 'paid'];

// The server sends amountPaid/balance as virtuals, but recompute locally so the
// UI is still right if an entry is updated optimistically.
export const paidTotal = (entry) =>
  (entry?.payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

export const balanceOf = (entry) =>
  (Number(entry?.invoiceAmount) || 0) - paidTotal(entry);

// Ledger-wide totals — the "Total Amount Paid" / "Total Amount Owed" cells
// from the spreadsheet.
export const ledgerTotals = (entries = []) => ({
  invoiced: entries.reduce((s, e) => s + (Number(e.invoiceAmount) || 0), 0),
  paid: entries.reduce((s, e) => s + paidTotal(e), 0),
  owed: entries.reduce((s, e) => s + Math.max(0, balanceOf(e)), 0),
});

// "in 43 days" / "43 days ago" / "today", in the viewer's language.
// Intl.RelativeTimeFormat handles every language's plural rules and word order
// for us, so there's nothing to translate by hand here.
export const expiryPhrase = (daysLeft) => {
  if (daysLeft == null) return '';
  try {
    return new Intl.RelativeTimeFormat(currentLanguage(), { numeric: 'auto' }).format(daysLeft, 'day');
  } catch {
    return daysLeft < 0 ? `${Math.abs(daysLeft)} days ago` : `in ${daysLeft} days`;
  }
};

// A document's display name: the three required types translate; a free-form
// "other" document keeps whatever the tech named it.
export const displayLabel = (item) =>
  item.type === 'other' ? (item.label || t('lg.type.other')) : credentialLabel(item.type);

// Colored pill for one document's state.
export const credentialTag = (item) => {
  // A document nobody demanded should not be red. An optional one that is
  // simply absent reads as neutral; everything else keeps its colour.
  const missingOptional = item.status === 'missing' && isOptional(item.type);
  const cls = missingOptional ? 'tag-slate'
    : ({ valid: 'tag-green', expiring: 'tag-amber', expired: 'tag-red', missing: 'tag-red' }[item.status] || 'tag-slate');
  const text = missingOptional
    ? t('lg.optionalNotNeeded', 'Optional')
    : item.status === 'missing'
    ? t('lg.notOnFile')
    : neverExpires(item.type) ? t('lg.onFile', 'On file') : expiryPhrase(item.daysLeft);
  return <span className={`tag ${cls}`}>{displayLabel(item)}: {text}</span>;
};

// Colored pill for a ledger row's state.
// ── What an invoice is actually for ────────────────────────────────────────
//
// An invoice's lines can live in two places, and a reader has to look in both.
// An invoice built in the technician's own editor carries its own `items`. One
// generated from an approved estimate carries none, and its detail sits on the
// estimate it came from — deliberately, so the two can never disagree about
// what was billed (see the note on estimateId in server/models/LedgerEntry).
//
// This is the one place that order is written down. It used to live inside
// openInvoicePaper in App.js and nowhere else, which is why Julian's ledger
// card showed "Shower door installation · $100" and nothing behind it: his
// side never looked past `jobDetails`.
export const invoiceLines = (entry) => {
  if (!entry) return { items: [], from: 'none', sourceLang: null };
  const est = entry.estimateId && typeof entry.estimateId === 'object' ? entry.estimateId : null;
  if ((entry.items || []).length) {
    return { items: entry.items, from: 'invoice', sourceLang: entry.sourceLang || null };
  }
  if ((est?.items || []).length) {
    return { items: est.items, from: 'estimate', sourceLang: est.sourceLang || entry.sourceLang || null, estimateNumber: est.estimateNumber };
  }
  // Raised before invoices had lines. The one-line summary IS the detail.
  return { items: [], from: 'summary', sourceLang: entry.sourceLang || null };
};

// Everything attached to an invoice, wherever it was attached: to a line, to
// the invoice itself, or as the technician's own uploaded invoice file.
// Counted so a button can say what is behind it — "see the detail" with
// nothing behind it is worse than no button.
export const invoiceAttachments = (entry) => {
  const { items } = invoiceLines(entry);
  const linePhotos = items.flatMap((i) => i.photos || []);
  const lineFiles = items.flatMap((i) => i.files || []);
  const docs = [
    ...(entry?.documents || []),
    ...(entry?.invoiceDocs || []).map((u) => ({ url: u, name: 'Their invoice' })),
  ];
  const progressPhotos = (entry?.progress || []).flatMap((p) => p.photos || []);
  return {
    linePhotos, lineFiles, docs, progressPhotos,
    photoCount: linePhotos.length + progressPhotos.length,
    fileCount: lineFiles.length + docs.length,
  };
};

export const ledgerStatusTag = (status) => {
  const cls = {
    'open': 'tag-slate',
    'in-progress': 'tag-blue',
    'completed': 'tag-teal',
    'paid': 'tag-green',
  }[status] || 'tag-slate';
  return <span className={`tag ${cls}`}>{t('lg.st.' + status, status)}</span>;
};
